import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { billingService, PaymentFilters } from '../../services/billingService';
import BillingFilters from './components/BillingFilters';
import PaymentTable from './components/PaymentTable';
import ExportButton from './components/ExportButton';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const BillingPage: React.FC = () => {
  const [filters, setFilters] = useState<PaymentFilters>({
    page: 1,
    limit: 20,
    status: '',
    startDate: '',
    endDate: '',
  });

  // Fetch payments using TanStack Query
  const { data, isLoading, error } = useQuery({
    queryKey: ['billingPayments', filters],
    queryFn: () => billingService.getPayments(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const handleStatusChange = (status: string) => {
    setFilters(prev => ({
      ...prev,
      status,
      page: 1, // Reset to first page when filter changes
    }));
  };

  const handleDateChange = (startDate: string, endDate: string) => {
    setFilters(prev => ({
      ...prev,
      startDate,
      endDate,
      page: 1, // Reset to first page when filter changes
    }));
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({
      ...prev,
      page: newPage,
    }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLimitChange = (newLimit: number) => {
    setFilters(prev => ({
      ...prev,
      limit: newLimit,
      page: 1, // Reset to first page when limit changes
    }));
  };

  if (error) {
    console.error('Failed to load payment history:', error);
  }

  return (
    <div className="min-h-screen bg-[#09140F] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Billing & Payments</h1>
          <p className="text-gray-400">Manage payment history and invoices</p>
        </div>

        {/* Filters and Actions */}
        <div className="bg-[#16251D] border border-gray-800 rounded-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-end justify-between">
            <div className="flex-1">
              <BillingFilters
                status={filters.status || ''}
                startDate={filters.startDate || ''}
                endDate={filters.endDate || ''}
                onStatusChange={handleStatusChange}
                onDateChange={handleDateChange}
              />
            </div>
            <ExportButton filters={filters} />
          </div>
        </div>

        {/* Payment Table */}
        <div className="bg-[#16251D] border border-gray-800 rounded-lg overflow-hidden">
          {error && (
            <div className="p-6 text-center text-red-400">
              Failed to load payment history. Please try again.
            </div>
          )}
          <PaymentTable
            payments={data?.data || []}
            isLoading={isLoading}
            error={error ? 'Failed to load payments' : undefined}
          />
        </div>

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between flex-wrap gap-4">
            <div className="text-gray-400 text-sm">
              Showing {((filters.page || 1) - 1) * (filters.limit || 20) + 1} to{' '}
              {Math.min((filters.page || 1) * (filters.limit || 20), data.total)} of {data.total}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange((filters.page || 1) - 1)}
                disabled={(filters.page || 1) <= 1}
                className="p-2 rounded-lg border border-gray-700 text-gray-400 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={20} />
              </button>

              <div className="flex gap-1">
                {(() => {
                  const current = filters.page || 1;
                  const total = data.totalPages;
                  const pages: number[] = [];
                  if (total <= 7) {
                    for (let i = 1; i <= total; i++) pages.push(i);
                  } else {
                    pages.push(1);
                    if (current > 3) pages.push(-1); // ellipsis
                    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
                    if (current < total - 2) pages.push(-2); // ellipsis
                    pages.push(total);
                  }
                  return pages.map((page, idx) =>
                    page < 0 ? (
                      <span key={`ellipsis-${idx}`} className="px-2 py-2 text-gray-500">…</span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          current === page
                            ? 'bg-[#23F47C] text-gray-900'
                            : 'border border-gray-700 text-gray-400 hover:bg-gray-800'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  );
                })()}
              </div>

              <button
                onClick={() => handlePageChange((filters.page || 1) + 1)}
                disabled={(filters.page || 1) >= data.totalPages}
                className="p-2 rounded-lg border border-gray-700 text-gray-400 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-gray-400 text-sm">Items per page:</label>
              <select
                value={filters.limit || 20}
                onChange={e => handleLimitChange(parseInt(e.target.value))}
                className="bg-gray-800 border border-gray-700 text-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#23F47C]"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingPage;
